// src/main.ts
import { bootstrapApplication } from '@angular/platform-browser';
import { importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeEsExtra from '@angular/common/locales/extra/es';
import dayjs from 'dayjs';
import localeEsDayJs from 'dayjs/locale/es';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { AppComponent } from './app/app.component';
import { PlatformService } from './app/services/platform.service';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { AppRoutingModule } from './app/app-routing.module';
import { TitleStrategy } from '@angular/router';
import { BisualTitleStrategy } from './app/title.strategy';

import { DEFAULT_CURRENCY_CODE, provideAppInitializer, inject } from '@angular/core';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { SecondsToStringTimePipe } from './app/pipes/seconds-to-string-time.pipe';
import { PendingChangesGuard } from './app/guards/pending-changes.guard';
import { HourStringToSecondsPipe } from './app/pipes/hour-string-to-seconds.pipe';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { SnackbarInterceptor } from './app/interceptors/snackbar.interceptor';
import { TokenInterceptor } from './app/interceptors/token.interceptor';
import { LoadingInterceptor } from './app/interceptors/loading.interceptor';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter, provideCalendar } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { MatCardModule } from '@angular/material/card';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTreeModule } from '@angular/material/tree';
import { MatChipsModule } from '@angular/material/chips';
import { EditorModule } from '@tinymce/tinymce-angular';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepperModule } from '@angular/material/stepper';
import { CdkStepperModule } from '@angular/cdk/stepper';
import isBetween from 'dayjs/plugin/isBetween';

export function initializeApp(platformService: PlatformService): () => void {
  return () => {};
}

// Register Angular locale data
registerLocaleData(localeEs, 'es', localeEsExtra);

// Global Day.js config
dayjs.locale(localeEsDayJs);
[isSameOrAfter, isSameOrBefore, isoWeek, dayOfYear, customParseFormat, utc, timezone, isBetween].forEach(plugin => dayjs.extend(plugin));

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      MatButtonModule,
      MatIconModule,
      MatMenuModule,
      MatFormFieldModule,
      MatAutocompleteModule,
      FormsModule,
      ReactiveFormsModule,
      MatInputModule,
      MatPaginatorModule,
      MatTableModule,
      MatExpansionModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MatProgressBarModule,
      MatSnackBarModule,
      MatCheckboxModule,
      MatSelectModule,
      MatTabsModule,
      MatDialogModule,
      MatRadioModule,
      MatTooltipModule,
      NgbModule,
      DragDropModule,
      MatCardModule,
      NgxDropzoneModule,
      MatButtonToggleModule,
      MatProgressSpinnerModule,
      MatMenuModule,
      MatTreeModule,
      MatChipsModule,
      EditorModule,
      MatSlideToggleModule,
      MatStepperModule,
      CdkStepperModule
    ),
    provideCalendar({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    // Modules
    MatDatepickerModule,
    //Pipes
    DatePipe,
    DecimalPipe,
    SecondsToStringTimePipe,
    PendingChangesGuard,
    HourStringToSecondsPipe,
    CurrencyPipe,
    // Interceptors
    { provide: HTTP_INTERCEPTORS, useClass: SnackbarInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    // Route Reuse Strategy
    // { provide: RouteReuseStrategy, useClass: BacklogRouteReuseStrategy },
    // Other providers
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    // CDK 21+ renders overlays with the native Popover API (top layer). TinyMCE menus
    // are appended to body, so they would appear behind MatDialog and look "broken".
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: TitleStrategy, useClass: BisualTitleStrategy },
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(PlatformService));
      return initializerFn();
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch(err => console.error(err));
